import { createMultiStyleConfigHelpers } from '@chakra-ui/styled-system'

import { inputTheme } from './input'

// This function creates a set of function that helps us create multipart component styles.
const helpers = createMultiStyleConfigHelpers([
  'control',
  'dropdownIndicator',
  'indicatorsContainer',
  'singleValue',
  'valueContainer',
  'placeholder',
  'option',
])

export const selectInputTheme = helpers.defineMultiStyleConfig({
  baseStyle: {
    selectContainer: {
      position: 'relative',
    },
    control: {
      display: 'flex',
      gap: '6px',
      justifyContent: 'space-between',
      alignItems: 'center',
      cursor: 'pointer',
      minWidth: 0,
      _disabled: {
        cursor: 'not-allowed',
        backgroundColor: 'presets.neutral.10',
      },
      _placeholder: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
    valueContainer: {
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      alignItems: 'center',
      lineHeight: '14px',
      fontSize: '12px',
      color: 'presets.neutral.90',
      minWidth: 0,
      overflow: 'hidden',
      flex: 1,
    },
    singleValue: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    placeholder: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    indicatorsContainer: {
      alignItems: 'center',
      display: 'flex',
    },
    dropdownIndicator: {
      transition: 'transform .4s ease',
      boxSize: '10px',
      color: 'presets.neutral.90',
    },
    menu: {
      backgroundColor: 'presets.neutral.0',
      marginTop: '2px',
      borderRadius: '6px',
      boxShadow: '0px 4px 3px 0px #00000008, 0px 3px 10px 0px #0000000D',
      padding: '0',
      color: 'presets.neutral.90',
    },
    menuList: {
      borderRadius: '6px',
      padding: '0',
      overflowY: 'auto',
      overflowX: 'hidden',
      maxHeight: '420px',
      border: '1px solid',
      borderColor: 'presets.neutral.20',
      scrollbarWidth: 'thin',
      scrollbarColor: 'var(--chakra-colors-presets-neutral-40) transparent',
      '&::-webkit-scrollbar': {
        display: 'block',
        width: '8px',
      },
      '&::-webkit-scrollbar-track': {
        background: 'transparent',
      },
      '&::-webkit-scrollbar-thumb': {
        background: 'presets.neutral.40',
        borderRadius: '4px',
        border: '2px solid transparent',
        backgroundClip: 'padding-box',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        background: 'presets.neutral.60',
      },
    },
    option: {
      _hover: {
        background: 'presets.neutral.10',
      },
      _selected: {
        fontWeight: '700',
        backgroundColor: 'presets.neutral.0',
      },
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '8px',
      fontSize: '12px',
      fontWeight: '600',
      padding: '11px 8px',
      cursor: 'pointer',
      borderBottom: '1px solid',
      borderColor: 'presets.neutral.20',
      _placeholder: {
        color: 'presets.neutral.50',
      },
    },
  },
  variants: {
    primary: {
      selectContainer: {},
      control: {
        ...inputTheme.baseStyle?.field,
        // 👇 minWidth 0 so select stays on one line in flex layouts (can shrink, text truncates)
        minWidth: 0,
        minHeight: '32px',
        display: 'flex',
        gap: '6px',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer',
        // 👆had to duplicate to override inputTheme
        color: 'presets.turquoise.100',
        paddingY: '8px',
        fontWeight: '500',
        _disabled: {
          cursor: 'not-allowed',
          backgroundColor: 'presets.neutral.10',
        },
        _focus: {
          borderColor: 'presets.turquoise.50',
          boxShadow: 'none',
        },
        _focusVisible: {
          borderColor: 'presets.turquoise.50',
        },
        _dark: {
          backgroundColor: 'presets.dark.surface',
          borderColor: 'presets.neutral.90',
          color: 'presets.neutral.0',
          _disabled: {
            color: 'presets.neutral.60',
            backgroundColor: 'presets.dark.surfacePanel',
            opacity: 0.8,
            cursor: 'not-allowed',
          },
          _placeholder: {
            color: 'presets.neutral.0',
          },
          _focus: {
            borderColor: 'presets.turquoise.50',
            boxShadow: 'none',
          },
          _focusVisible: {
            borderColor: 'presets.turquoise.50',
          },
        },
      },
      valueContainer: {
        _dark: {
          color: 'presets.neutral.0',
        },
      },
      singleValue: {
        _dark: {
          color: 'presets.neutral.0',
        },
      },
      placeholder: {
        _dark: {
          color: 'presets.neutral.0',
        },
      },
      indicatorsContainer: {},
      dropdownIndicator: {
        color: 'presets.neutral.90',
        _dark: {
          color: 'presets.neutral.0',
        },
      },
      menu: {
        _dark: {
          backgroundColor: 'presets.dark.surface',
          border: '1px solid',
          borderColor: 'presets.neutral.90',
          color: 'presets.neutral.0',
          boxShadow: '0 4px 24px 0 rgba(0, 0, 0, 0.24)',
        },
      },
      menuList: {
        _dark: {
          borderColor: 'presets.neutral.90',
          backgroundColor: 'presets.dark.surface',
        },
      },
      option: {
        _dark: {
          backgroundColor: 'presets.dark.surface',
          color: 'presets.neutral.0',
          borderBottom: '0.5px solid',
          borderColor: 'presets.neutral.90',
          _hover: {
            background: 'presets.overlay.neutral10_10',
            borderBottom: '0.5px solid',
            borderColor: 'presets.neutral.90',
          },
          _selected: {
            backgroundColor: 'presets.dark.surface',
          },
        },
      },
    },
    secondary: {
      control: {
        borderRadius: '6px',
        px: '4px',
        _hover: {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
        _placeholder: {
          color: 'presets.turquoise.100',
          _dark: {
            color: 'white',
          },
        },
        _dark: {
          color: 'white',
        },
      },
      valueContainer: {},
      singleValue: {
        fontWeight: '600',
      },
      indicatorsContainer: {
        alignItems: 'center',
        display: 'flex',
      },
      dropdownIndicator: {
        transition: 'transform .4s ease',
        boxSize: '12px',
        color: 'presets.neutral.60',
        _dark: {
          color: 'white',
        },
      },
      menu: {
        backgroundColor: 'presets.neutral.0',
        borderRadius: '4px',
        boxShadow:
          '0px 6px 20px 0px rgba(0, 0, 0, 0.08), 0px 8px 30px 0px rgba(0, 0, 0, 0.10)',
        padding: '0',
        color: 'presets.turquoise.100 !important',
      },

      menuList: {
        padding: '0',
        scrollbarWidth: 'thin',
        scrollbarColor: 'var(--chakra-colors-presets-neutral-40) transparent',
        '&::-webkit-scrollbar': {
          display: 'block',
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'presets.neutral.40',
          borderRadius: '4px',
          border: '2px solid transparent',
          backgroundClip: 'padding-box',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: 'presets.neutral.60',
        },
      },

      option: {
        _hover: {
          background: 'presets.neutral.10',
        },
        _selected: {
          fontWeight: '700',
        },
        fontSize: '14px',
        fontWeight: '500',
        padding: '8px 12px',
        cursor: 'pointer',
      },
    },
    /** No focus highlight (border stays neutral). Use for filter condition dropdown only. */
    filterCondition: {
      selectContainer: {},
      control: {
        ...inputTheme.baseStyle?.field,
        minWidth: 0,
        minHeight: '32px',
        display: 'flex',
        gap: '6px',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer',
        color: 'presets.turquoise.100',
        paddingY: '8px',
        fontWeight: '500',
        _disabled: {
          cursor: 'not-allowed',
          backgroundColor: 'presets.neutral.10',
        },
        _focus: {
          borderColor: 'presets.neutral.20',
          boxShadow: 'none',
        },
        _focusVisible: {
          borderColor: 'presets.neutral.20',
        },
        _dark: {
          backgroundColor: 'presets.dark.surface',
          borderColor: 'presets.neutral.90',
          color: 'presets.neutral.0',
          _disabled: {
            color: 'presets.neutral.60',
            backgroundColor: 'presets.dark.surfacePanel',
            opacity: 0.8,
            cursor: 'not-allowed',
          },
          _placeholder: {
            color: 'presets.neutral.0',
          },
          _focus: {
            borderColor: 'presets.neutral.90',
            boxShadow: 'none',
          },
          _focusVisible: {
            borderColor: 'presets.neutral.90',
          },
        },
      },
      valueContainer: {
        _dark: {
          color: 'presets.neutral.0',
        },
      },
      singleValue: {
        _dark: {
          color: 'presets.neutral.0',
        },
      },
      placeholder: {
        _dark: {
          color: 'presets.neutral.0',
        },
      },
      indicatorsContainer: {},
      dropdownIndicator: {
        color: 'presets.neutral.90',
        _dark: {
          color: 'presets.neutral.0',
        },
      },
      menu: {
        _dark: {
          backgroundColor: 'presets.dark.surface',
          border: '1px solid',
          borderColor: 'presets.neutral.90',
          color: 'presets.neutral.0',
          boxShadow: '0 4px 24px 0 rgba(0, 0, 0, 0.24)',
        },
      },
      menuList: {
        _dark: {
          borderColor: 'presets.neutral.90',
          backgroundColor: 'presets.dark.surface',
        },
      },
      option: {
        _dark: {
          backgroundColor: 'presets.dark.surface',
          color: 'presets.neutral.0',
          borderBottom: '0.5px solid',
          borderColor: 'presets.neutral.90',
          _hover: {
            background: 'presets.overlay.neutral10_10',
            borderBottom: '0.5px solid',
            borderColor: 'presets.neutral.90',
          },
          _selected: {
            backgroundColor: 'presets.dark.surface',
          },
        },
      },
    },
  },
  defaultProps: {
    // size: 'md',
    variant: 'primary',
  },
})
